import axios from 'axios';

// Configure API base URL
const API_BASE_URL = window.location.hostname === 'qtf.iniserve.com' 
  ? 'https://qtf.iniserve.com' 
  : (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000');



const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// REQUEST INTERCEPTOR: Attach Token to all requests
apiClient.interceptors.request.use(
  (config) => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        if (user.token) {
          config.headers.Authorization = `Bearer ${user.token}`;
        }
      }
    } catch (err) {
      console.warn('[API Client] Failed to attach token:', err.message);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let onSessionInvalid = null;

/** Register logout handler from AuthContext (avoids circular import issues). */
export function setSessionInvalidHandler(handler) {
  onSessionInvalid = handler;
}

function redirectToLoginAfterAuthFailure() {
  const path = window.location.pathname || '';
  if (path.includes('/login')) return;
  const base =
    path === '/admin' || path.startsWith('/admin/')
      ? '/admin'
      : path === '/qft' || path.startsWith('/qft/')
        ? '/qft'
        : '';
  window.location.replace(`${base}/login?reason=session`);
}

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const msg = error.response?.data?.message || error.response?.data?.error;
    if (msg) {
      error.message = msg;
    }

    // Don't hijack the login request itself — let LoginPage surface the reason
    // (e.g. "Account is disabled") instead of bouncing to the session screen.
    const reqUrl = String(error.config?.url || '');
    const isLoginRequest =
      reqUrl.includes('/auth/google-login') ||
      reqUrl.includes('/auth/login') ||
      reqUrl.endsWith('/login');

    if (!isLoginRequest && (status === 401 || status === 403)) {
      const authMsg = String(msg || '').toLowerCase();
      const authCode = error.response?.data?.code;
      const isAuthFailure =
        status === 401 ||
        authCode === 'ACCOUNT_DISABLED' ||
        authCode === 'TOKEN_EXPIRED' ||
        authCode === 'TOKEN_INVALID' ||
        authMsg.includes('authenticate token') ||
        authMsg.includes('no token provided') ||
        authMsg.includes('session expired') ||
        authMsg.includes('account is disabled');

      if (isAuthFailure) {
        localStorage.removeItem('user');
        if (typeof onSessionInvalid === 'function') {
          onSessionInvalid();
        }
        redirectToLoginAfterAuthFailure();
      }
    }

    return Promise.reject(error);
  }
);

// Rep Targets API
export const repTargetsAPI = {
  // Get all targets
  getAll: () => apiClient.get('/api-v1/admin/rep-targets'),

  // Target vs achieved for one month (paired values from DB)
  getMonthSummary: (month) =>
    apiClient.get('/api-v1/admin/rep-targets/month-summary', { params: { month } }),

  // Get specific rep targets
  getByRepId: (repId) => apiClient.get(`/api-v1/admin/rep-targets/${repId}`),

  // Create/set targets
  create: (data) => apiClient.post('/api-v1/admin/rep-targets', data),

  // Update targets
  update: (repId, data) => apiClient.put(`/api-v1/admin/rep-targets/${repId}`, data),
};

// Rep Progress API
export const repProgressAPI = {
  // Get rep progress (monthly)
  getProgress: (repId, month) =>
    apiClient.get(`/api-v1/admin/rep-progress/${repId}`, { params: { month } }),

  // Get all historical sales for a rep
  getHistory: (repId) =>
    apiClient.get(`/api-v1/admin/rep-progress-history/${repId}`),

  // Record sales and calculate bonus/penalty
  recordSales: (data) => apiClient.post('/api-v1/admin/rep-progress/update', data),

  // Auto-derived sales volume from delivered orders (read-only)
  getSalesFromOrders: (repId, month) =>
    apiClient.get('/api-v1/admin/rep-sales-from-orders', { params: { rep_id: repId, month } }),

  // Recompute sales from delivered orders and persist bonus/penalty
  syncFromOrders: (data) => apiClient.post('/api-v1/admin/rep-progress/sync-from-orders', data),
};

// Broadcasts & scheduled notifications (announcements + reminders)
export const broadcastsAPI = {
  getAll: (limit = 100) =>
    apiClient.get('/api-v1/admin/broadcasts', { params: { limit } }),
  create: (data) => apiClient.post('/api-v1/admin/broadcasts', data),
  sendNow: (id) => apiClient.post(`/api-v1/admin/broadcasts/${id}/send-now`),
  update: (id, data) => apiClient.put(`/api-v1/admin/broadcasts/${id}`, data),
  remove: (id) => apiClient.delete(`/api-v1/admin/broadcasts/${id}`),
};

// Admin Management API
export const adminAPI = {
  // Get all reps
  getAllReps: () => apiClient.get('/api-v1/admin/reps'),

  // Reps for order forms — includes inactive "Direct Order" system rep
  getOrderReps: () => apiClient.get('/api-v1/admin/reps', { params: { for_orders: 1 } }),

  getRepLocations: () => apiClient.get('/api-v1/admin/rep-locations'),

  getOverviewStats: () => apiClient.get('/api-v1/admin/dashboard/overview-stats'),

  getAnalyticsMonthExtras: (params = {}) =>
    apiClient.get('/api-v1/admin/analytics/month-extras', { params }),

  getOrderSourceAnalytics: (params = {}) =>
    apiClient.get('/api-v1/admin/analytics/order-sources', { params }),

  // Get all customers
  getAllCustomers: () => apiClient.get('/api-v1/admin/customers'),

  // Customer Management
  createCustomer: (data) => apiClient.post('/api-v1/admin/customers', data),
  updateCustomer: (id, data) => apiClient.put(`/api-v1/admin/customers/${id}`, data),
  deleteCustomer: (id) => apiClient.delete(`/api-v1/admin/customers/${id}`),

  // User Management
  getAllUsers: () => apiClient.get('/api-v1/admin/users'),
  createUser: (data) => apiClient.post('/api-v1/admin/users', data),
  updateUser: (id, data) => apiClient.put(`/api-v1/admin/users/${id}`, data),
  deleteUser: (id) => apiClient.delete(`/api-v1/admin/users/${id}`),
  getDivisions: () => apiClient.get('/api-v1/admin/divisions'),
  createDivision: (data) => apiClient.post('/api-v1/admin/divisions', data),
  updateDivision: (id, data) => apiClient.put(`/api-v1/admin/divisions/${id}`, data),

  // Order Management
  getAllOrders: () => apiClient.get('/api-v1/admin/orders'),
  getOrdersByCustomer: (customerId) => apiClient.get(`/api-v1/admin/orders/customer/${customerId}`),
  createOrder: (data) => apiClient.post('/api-v1/admin/orders', data),
  updateOrder: (id, data) => apiClient.put(`/api-v1/admin/orders/${id}`, data),
  deleteOrder: (id) => apiClient.delete(`/api-v1/admin/orders/${id}`),
  updateOrderItem: (id, data) => apiClient.put(`/api-v1/admin/order-items/${id}`, data),

  // Order payments (partial payments + receipts)
  getOrderPayments: (orderId) => apiClient.get(`/api-v1/admin/orders/${orderId}/payments`),
  addOrderPayment: (orderId, data) => apiClient.post(`/api-v1/admin/orders/${orderId}/payments`, data),
  deleteOrderPayment: (paymentId) => apiClient.delete(`/api-v1/admin/payments/${paymentId}`),

  // Reset device
  resetDevice: (userId) =>
    apiClient.post('/api-v1/admin/reset-device', { user_id: userId }),

  // Attendance (punch in / out)
  getAttendance: (params = {}) =>
    apiClient.get('/api-v1/admin/attendance', { params }),
  getAttendanceMonthSummary: (params = {}) =>
    apiClient.get('/api-v1/admin/attendance/month-summary', { params }),

  // Visit history
  getVisitLogs: (params = {}) =>
    apiClient.get('/api-v1/admin/visit-logs', { params }),

  getVisitLog: (id) => apiClient.get(`/api-v1/admin/visit-logs/${id}`),

  // Fuel expenses
  getFuelLogs: (params = {}) =>
    apiClient.get('/api-v1/admin/fuel-logs', { params }),

  // Leave management
  getLeaveRequests: (params = {}) =>
    apiClient.get('/api-v1/admin/leave', { params }),
  getLeaveMonthSummary: (params = {}) =>
    apiClient.get('/api-v1/admin/leave/month-summary', { params }),
  reviewLeave: (id, status, reviewNote = '') =>
    apiClient.put(`/api-v1/admin/leave/${id}`, { status, review_note: reviewNote }),
};

// Product Catalog API (admin-managed materials + sub-items)
export const productsAPI = {
  // Active catalog for order entry (any authenticated user)
  getActive: () => apiClient.get('/api-v1/products'),
  // Full catalog incl. inactive (admin management screen)
  getAll: () => apiClient.get('/api-v1/admin/products'),
  create: (data) => apiClient.post('/api-v1/admin/products', data),
  update: (id, data) => apiClient.put(`/api-v1/admin/products/${id}`, data),
  remove: (id) => apiClient.delete(`/api-v1/admin/products/${id}`),
  reorder: (ids) => apiClient.put('/api-v1/admin/products/reorder', { ids }),
};

// Product incentive rates (₹ per unit × quantity sold)
export const productIncentivesAPI = {
  getAll: () => apiClient.get('/api-v1/admin/product-incentives'),
  saveAll: (data) => apiClient.put('/api-v1/admin/product-incentives', data),
};

// System API
export const systemAPI = {
  // Get settings
  getSettings: () => apiClient.get('/api-v1/settings'),

  // Update settings
  updateSettings: (data) => apiClient.put('/api-v1/settings', data),

  // Health check
  healthCheck: () => apiClient.get('/test'),

  // Welcome (list all APIs)
  getWelcome: () => apiClient.get('/'),

  // Server health (DB connectivity, uptime, memory)
  getHealth: () => apiClient.get('/api-v1/health'),

  // Persisted server logs (admin only). type: 'error' | 'access'; level filters severity.
  getLogs: (type = 'error', limit = 100, level = 'all') =>
    apiClient.get('/api-v1/admin/logs', { params: { type, limit, level } }),

  // 24h error/request digest. send=true also pushes it to the webhook.
  getDigest: (send = false) =>
    apiClient.get('/api-v1/admin/logs/digest', { params: send ? { send: 1 } : {} }),

  // Admin notification feed. since=<id> returns only newer items (for polling).
  getNotifications: (since = 0, limit = 30) =>
    apiClient.get('/api-v1/admin/notifications', {
      params: { ...(since ? { since } : {}), limit },
    }),

  // Web push (VAPID) — background notifications.
  getPushKey: () => apiClient.get('/api-v1/admin/push/public-key'),
  subscribePush: (subscription) =>
    apiClient.post('/api-v1/admin/push/subscribe', { subscription }),
};

// Salary API
export const salaryAPI = {
  // Get all salary slips
  getAllSlips: (params) => apiClient.get('/api-v1/admin/salary-slips', { params }),

  // Calculate salary from live orders + product incentives + target rules
  calculate: (month) => apiClient.post('/api-v1/admin/salary-slips/calculate', { month }),

  // Save/Create a salary slip
  createSlip: (data) => apiClient.post('/api-v1/admin/salary-slips', data),

  // Update payment status
  updatePaymentStatus: (id, status, paymentDate) => 
    apiClient.put(`/api-v1/admin/salary-slips/${id}`, { payment_status: status, payment_date: paymentDate }),
};

// Export client as named export
export const client = apiClient;

export default apiClient;
